import styled from 'styled-components';
import i18n from 'src/utils/translate';
import { textSmall, titleSmall } from 'styles-js/mixins/typography';

import UserLink from 'src/modules/shared/links/userLink';
import Roles from 'src/components/roles';
import RelativeTime from 'src/components/relativeTime';
import Button from 'src/components/buttons/button';

import { Section, Element } from 'src/constants/footfall';
import { Author } from 'src/constants/types';
import { Size } from 'src/components/avatar';

type ReplyHeaderProps = {
  author: Author;
  dateCreated: string;
  inReplyToUsername?: string;
  onCancel?: () => void;
}

export default function ReplyItemHeader({
  author,
  dateCreated,
  inReplyToUsername,
  onCancel = null,
}: ReplyHeaderProps): JSX.Element {
  return (
    <Header>
      <UserLink
        avatarSize={Size.Small}
        className="userlink-avatar-link"
        signupContext={{
          section: Section.POST_REPLY,
          element: Element.AVATAR,
        }}
        user={author}
      />
      <Text>
        <UserLinkAuthor
          signupContext={{ element: Element.USER_LINK, section: Section.POST_REPLY }}
          user={author}
        />
        <Roles className="badge" roles={author.roles} />
        {inReplyToUsername && (
          <>
            <span className="desktop"> {i18n.t('in reply to')} </span>
            <span className="mobile icon icon-in-reply" />
            <UserLinkReply
              signupContext={{ element: Element.USER_LINK, section: Section.POST_REPLY }}
              user={{ username: inReplyToUsername }}
            />
          </>
        )}
        <RelativeTime rawTime={dateCreated} />
      </Text>

      {!!onCancel && (
        <CancelEditButton type="none" onClick={onCancel}>
          <span className="icon icon-cross-2" title="Close" />
        </CancelEditButton>
      )}
    </Header>
  );
}

const Header = styled.div`
  position: relative;
  margin-bottom: 5px;

  .userlink-avatar-link {
    width: 40px;
  }
`;

const Text = styled.div`
  display: inline-block;
  vertical-align: top;
  width: calc(100% - 50px);
  margin-left: 10px;
  padding-right: 20px;
  ${textSmall};
  line-height: ${({ theme }) => theme.fontLineHeightS};
  color: ${({ theme }) => theme.colorTextLight};

  .badge {
    margin-left: 10px;
  }
  time {
    display: block;
  }

  .desktop {
    @media (${({ theme }) => theme.underScreenSmall}) {
      display: none;
    }

    &:before {
      content:'•';
      margin: 0 5px;
      color: ${({ theme }) => theme.colorGreyLight};
      font-size: 20px;
    }
  }

  .mobile {
    @media (${({ theme }) => theme.overScreenSmall}) {
      display: none;
    }
  }
  .icon {
    font-size: 17px;
  }
`;

const UserLinkAuthor = styled(UserLink)`
  ${titleSmall};
  line-height: ${({ theme }) => theme.fontLineHeightSWide};

  @media (${({ theme }) => theme.underScreenSmall}) {
    ${textSmall};
    font-weight: ${({ theme }) => theme.fontWeightBold};
  }
`;

const UserLinkReply = styled(UserLink)`
  ${textSmall};
  color: ${({ theme }) => theme.colorTextLight};
`;

const CancelEditButton = styled(Button)`
  position: absolute;
  top: 0;
  right: -10px;
  font-size: ${props => props.theme.fontSizeM};
  @media (${props => props.theme.underScreenSmall}) {
    padding-top: 0;
  }
`;
